/**
 * hostStorage — the one seam between the bundle and "where small preferences
 * live" (viewer state, per-space viewport memory, board switches, overlays).
 *
 * The bundle used to reach `localStorage` directly from a dozen call sites.
 * That holds while it runs on a stable web origin, but a native webview gets a
 * per-instance origin: two editor tabs of the same workspace would each keep
 * their own copy, and a disposed webview can lose it entirely. The symptom is
 * silent — the camera just resets on reload — so the fix is a seam, not a
 * guard at each site.
 *
 * Default backend is `localStorage` (unchanged behaviour on the web, and a
 * memory fallback if the origin denies it). A host that owns durable storage
 * installs {@link hostBackedStorage} at boot instead.
 *
 * Reads are SYNCHRONOUS by contract: the viewer reads persisted state at module
 * load, so a host backend is seeded with a snapshot up front (`arch:init`) and
 * mirrors writes outward, rather than making callers await.
 */

export interface StorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Enumerate keys — the viewport memory prunes its own key space. */
  keys(): readonly string[];
}

export function memoryStorageBackend(
  seed?: Readonly<Record<string, string>>,
): StorageBackend {
  const map = new Map<string, string>(seed ? Object.entries(seed) : []);
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
    keys: () => [...map.keys()],
  };
}

/**
 * The default backend. Resolves `localStorage` on EVERY call rather than
 * capturing it once: a captured reference goes stale the moment anything
 * swaps the global (jsdom suites installing a mock store do exactly that),
 * and the failure is a silent read of the wrong store. A property lookup per
 * preference read is free next to that.
 */
const liveLocalStorage: StorageBackend = {
  getItem: (key) => globalThis.localStorage?.getItem(key) ?? null,
  setItem: (key, value) => globalThis.localStorage?.setItem(key, value),
  removeItem: (key) => globalThis.localStorage?.removeItem(key),
  keys: () =>
    globalThis.localStorage ? Object.keys(globalThis.localStorage) : [],
};

let backend: StorageBackend | null = null;
let generation = 0;

function current(): StorageBackend {
  return backend ?? liveLocalStorage;
}

/**
 * Which backend is installed, as a monotonic counter.
 *
 * A caller that reads a preference ONCE and caches it (the viewer's boot
 * snapshot) has to know when that cache went stale — and installing a backend
 * is precisely the host saying "preferences live somewhere else now", which
 * invalidates anything read from the previous one.
 */
export function storageGeneration(): number {
  return generation;
}

/** Install a backend. Hosts do it at boot, tests use it to isolate. */
export function setStorageBackend(next: StorageBackend | null): void {
  backend = next;
  generation += 1;
}

/**
 * A backend whose reads are served locally from `seed` (so module-load reads
 * stay synchronous) and whose writes are ALSO mirrored to the host, which
 * persists them somewhere a webview origin can't reach.
 */
export function hostBackedStorage(
  seed: Readonly<Record<string, string>>,
  mirror: (key: string, value: string | null) => void,
): StorageBackend {
  const local = memoryStorageBackend(seed);
  return {
    getItem: (key) => local.getItem(key),
    setItem: (key, value) => {
      local.setItem(key, value);
      mirror(key, value);
    },
    removeItem: (key) => {
      local.removeItem(key);
      mirror(key, null);
    },
    keys: () => local.keys(),
  };
}

export const hostStorage = {
  getItem: (key: string): string | null => {
    try {
      return current().getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      current().setItem(key, value);
    } catch {
      // Quota or a denied origin — preferences are best-effort by design.
    }
  },
  removeItem: (key: string): void => {
    try {
      current().removeItem(key);
    } catch {
      // as above
    }
  },
  keys: (): readonly string[] => {
    try {
      return current().keys();
    } catch {
      return [];
    }
  },
};
